import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BadgeCheck, Star, Users, Share2, Instagram, Youtube, Twitter, LayoutDashboard, Quote, X, MonitorPlay, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { creator as mockCreator, testimonials } from '../mock';
import { API } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { QuizBlock, FlashDropBlock } from '../components/FunnelBlocks';
import CheckoutModal from '../components/CheckoutModal';
import AIConcierge from '../components/AIConcierge';
import { toast } from 'sonner';

const socialIcon = { instagram: Instagram, youtube: Youtube, twitter: Twitter, tiktok: Share2 };

export default function Storefront({ demoMode = false }) {
  const { username } = useParams();
  const handle = (username || 'aria.mercer').toLowerCase();
  const [selected, setSelected] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    setLoading(true);
    API.get(`/store/${handle}`)
      .then(({ data }) => { if (active) { setStore(data); setNotFound(false); } })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [handle]);

  const openCheckout = (product) => { setSelected(product); setCheckoutOpen(true); };

  if (loading) {
    return <div className="luxe-bg flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (notFound || !store) {
    return (
      <div className="luxe-bg flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Store not found</h1>
        <p className="text-white/60">biocloser.com/{handle} isn't claimed yet.</p>
        <Button onClick={() => navigate('/onboarding')} className="rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90">Claim this handle</Button>
      </div>
    );
  }

  const profile = store.profile || {};
  const products = (store.products || []).map((p) => ({ ...p, id: p.product_id, rating: p.rating || 5.0 }));
  const plan = store.plan || 'free';

  const inner = (
    <>
      {!demoMode && (
        <div className="sticky top-0 z-30 mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div className="glass flex items-center gap-2 rounded-full px-3 py-1.5">
            <span className="text-sm font-semibold text-white">biocloser.com/</span>
            <span className="text-sm text-primary">{store.handle}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/${store.handle}`); toast.success('Store link copied!'); }} size="icon" variant="ghost" className="glass h-9 w-9 rounded-full text-white hover:bg-white/10">
              <Share2 className="h-4 w-4" />
            </Button>
            <Link to="/dashboard">
              <Button size="sm" className="glass rounded-full text-white hover:bg-white/10">
                <LayoutDashboard className="mr-1.5 h-4 w-4" /> Dashboard
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-xl px-4 pt-6">
        <div className="animate-fade-up flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-primary/40 to-transparent blur-md" />
            <img src={profile.avatar || mockCreator.avatar} alt={store.name} className="relative h-24 w-24 rounded-full border-2 border-white/10 object-cover" />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold text-white">{store.name}</h1>
            <BadgeCheck className="h-5 w-5 fill-primary text-primary-foreground" />
          </div>
          <p className="mt-0.5 text-sm font-medium text-primary">{profile.tagline}</p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/70">{profile.bio}</p>

          <div className="mt-4 flex items-center gap-5">
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Users className="h-4 w-4 text-muted-foreground" /> <span className="font-semibold text-white">{mockCreator.followers}</span> followers
            </div>
            <div className="h-4 w-px bg-white/15" />
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Star className="h-4 w-4 fill-primary text-primary" /> <span className="font-semibold text-white">{mockCreator.rating}</span> ({mockCreator.reviews})
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            {mockCreator.socials.map((s) => {
              const Icon = socialIcon[s.platform] || Share2;
              return (
                <button key={s.platform} onClick={() => toast('Opening ' + s.handle)} className="glass transition-luxe flex h-9 w-9 items-center justify-center rounded-full text-white/80 hover:text-primary">
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          <FlashDropBlock onSelectProduct={openCheckout} />
          <QuizBlock onSelectProduct={openCheckout} products={products} handle={store.handle} />
        </div>

        <div className="mt-8">
          <h2 className="mb-3 px-1 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">Shop the offers</h2>
          <div className="space-y-3">
            {products.map((p, i) => (<ProductCard key={p.id} product={p} index={i} onSelect={openCheckout} />))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="mb-3 px-1 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">What clients say</h2>
          <div className="space-y-3">
            {testimonials.map((t, i) => (
              <div key={i} className="glass animate-fade-up rounded-2xl p-4" style={{ animationDelay: `${i * 80}ms` }}>
                <Quote className="h-5 w-5 text-primary/60" />
                <p className="mt-2 text-sm leading-relaxed text-white/85">{t.text}</p>
                <div className="mt-3 flex items-center gap-2">
                  <img src={t.avatar} alt={t.name} className="h-8 w-8 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-medium text-white">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                  <div className="ml-auto flex">{[...Array(5)].map((_, s) => <Star key={s} className="h-3.5 w-3.5 fill-primary text-primary" />)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-1 pb-8 text-center">
          <div className="glass rounded-full px-4 py-2 text-xs text-white/60">
            ⚡ Powered by <span className="font-semibold text-white">BioCloser</span> • Build your AI storefront free
          </div>
        </div>
      </div>
    </>
  );

  if (demoMode) {
    return (
      <div className="luxe-bg flex min-h-screen w-full flex-col items-center justify-start py-6">
        <div className="mb-4 flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/80">
          <MonitorPlay className="h-4 w-4 text-primary" /> Demo Preview · recording-ready
          <button onClick={() => navigate('/dashboard')} className="ml-2 flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs text-white hover:bg-white/15">
            <X className="h-3 w-3" /> Exit
          </button>
        </div>
        <div className="relative w-full max-w-[400px] px-4">
          <div className="relative overflow-hidden rounded-[2.5rem] border-4 border-white/10 bg-[#0B0F17] shadow-2xl">
            <div className="absolute left-1/2 top-2 z-40 h-5 w-28 -translate-x-1/2 rounded-full bg-black" />
            <div className="no-scrollbar h-[760px] overflow-y-auto">
              <div className="luxe-bg min-h-full pb-24 pt-8">{inner}</div>
              <AIConcierge autoDemo handle={store.handle} />
            </div>
          </div>
        </div>
        <CheckoutModal product={selected} open={checkoutOpen} onOpenChange={setCheckoutOpen} plan={plan} handle={store.handle} />
      </div>
    );
  }

  return (
    <div className="luxe-bg min-h-screen w-full">
      {inner}
      <CheckoutModal product={selected} open={checkoutOpen} onOpenChange={setCheckoutOpen} plan={plan} handle={store.handle} />
      <AIConcierge handle={store.handle} />
    </div>
  );
}
